import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Input, InputNumber, Radio, Select, Switch, Tooltip } from 'antd';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BUTTON_BORDER_RADIUS_MAX,
  CONTACT_FORM_MODES,
  PLACEMENTS,
  WIDGET_BORDER_WIDTH_MAX,
} from '@modules/presets/lib/constants';
import { Text } from '@shared/ui/antd';
import type { PresetFormValues } from '../../../lib/types';
import { ColorField } from '../color-field';
import { ContactFormFieldsEditor } from '../contact-form-fields-editor';
import { DynamicStringList } from '../dynamic-string-list';
import { PresetFormItem } from '../form-item';
import { SliderNumberField } from '../slider-number-field';
import styles from '../preset-form.module.scss';

interface AdvancedSettingsProps {
  control: Control<PresetFormValues>;
  errors: FieldErrors<PresetFormValues>;
  disabled?: boolean;
}

export const AdvancedSettings = ({ control, errors, disabled }: AdvancedSettingsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor.advanced' });
  const contactFormMode = useWatch({ control, name: 'advanced.contact_form.mode' });
  const showContactFormCloseButton = useWatch({
    control,
    name: 'advanced.contact_form.show_close_button',
  });

  return (
    <Flex vertical gap={16}>
      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('contactForm.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          <PresetFormItem label={t('contactForm.mode')} fullWidth required>
            <Controller
              control={control}
              name="advanced.contact_form.mode"
              render={({ field }) => (
                <Radio.Group
                  {...field}
                  disabled={disabled}
                  optionType="button"
                  buttonStyle="solid"
                  options={CONTACT_FORM_MODES.map((mode) => ({
                    label: t(`contactForm.modes.${mode}`),
                    value: mode,
                  }))}
                  className={styles.segmentedControl}
                />
              )}
            />
          </PresetFormItem>

          {!showContactFormCloseButton && (
            <PresetFormItem
              label={t('contactForm.message')}
              description={t('contactForm.messageDescription')}
              error={errors.advanced?.contact_form?.message_before_form?.message}
              fullWidth
              required
            >
              <Controller
                control={control}
                name="advanced.contact_form.message_before_form"
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    disabled={disabled}
                    placeholder={t('contactForm.messagePlaceholder')}
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    maxLength={1000}
                    showCount
                  />
                )}
              />
            </PresetFormItem>
          )}

          <PresetFormItem
            label={
              <span>
                {t('contactForm.showCloseButton')}{' '}
                <Tooltip title={t('contactForm.showCloseButtonDescription')} placement="top">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
          >
            <Controller
              control={control}
              name="advanced.contact_form.show_close_button"
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  disabled={disabled}
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>

          <PresetFormItem
            label={
              <span>
                {t('contactForm.showAfterAnswer')}
                <Tooltip title={t('contactForm.showAfterAnswerDescription')} placement="top">
                  <InfoCircleOutlined className={styles.helpIcon} />
                </Tooltip>
              </span>
            }
          >
            <Controller
              control={control}
              name="advanced.contact_form.show_form_after_answer"
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  disabled={disabled}
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>

          {contactFormMode === 'by_messages' ? (
            <PresetFormItem
              label={t('contactForm.afterMessages')}
              error={errors.advanced?.contact_form?.show_after_messages?.message}
              required
            >
              <Controller
                control={control}
                name="advanced.contact_form.show_after_messages"
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    min={0}
                    max={1000}
                    disabled={disabled}
                    placeholder={t('contactForm.afterMessagesPlaceholder')}
                    className={styles.numberInput}
                  />
                )}
              />
            </PresetFormItem>
          ) : (
            <PresetFormItem
              label={t('contactForm.triggerEvents')}
              error={errors.advanced?.contact_form?.trigger_events?.message}
              fullWidth
              required
            >
              <DynamicStringList
                control={control}
                name="advanced.contact_form.trigger_events"
                placeholder={t('contactForm.triggerEventPlaceholder')}
                addLabel={t('contactForm.addTriggerEvent')}
                disabled={disabled}
              />
            </PresetFormItem>
          )}
        </div>
        <ContactFormFieldsEditor control={control} disabled={disabled} />
      </div>

      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('widget.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          <PresetFormItem
            label={t('widget.width')}
            error={errors.advanced?.widget?.width_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.widget.width_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={320}
                  max={800}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('widget.height')}
            error={errors.advanced?.widget?.height_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.widget.height_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={400}
                  max={1000}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('widget.borderWidth')}
            error={errors.advanced?.widget?.border_width_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.widget.border_width_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={WIDGET_BORDER_WIDTH_MAX}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('widget.borderColor')}
            error={errors.advanced?.widget?.border_color?.message}
          >
            <Controller
              control={control}
              name="advanced.widget.border_color"
              render={({ field }) => (
                <ColorField value={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
        </div>
      </div>

      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('messages.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          <PresetFormItem
            label={t('messages.borderRadius')}
            error={errors.advanced?.messages?.border_radius?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.messages.border_radius"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={40}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem label={t('messages.tail')}>
            <Controller
              control={control}
              name="advanced.messages.tail_enabled"
              render={({ field }) => (
                <Switch checked={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
          <PresetFormItem label={t('messages.agentHints')}>
            <Controller
              control={control}
              name="advanced.messages.show_agent_answer_hints"
              render={({ field }) => (
                <Switch checked={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
        </div>
      </div>

      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('button.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          <PresetFormItem
            label={t('button.borderRadius')}
            error={errors.advanced?.button?.border_radius?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.button.border_radius"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={BUTTON_BORDER_RADIUS_MAX}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('button.width')}
            error={errors.advanced?.button?.width_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.button.width_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={100}
                  max={400}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('button.height')}
            error={errors.advanced?.button?.height_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.button.height_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={32}
                  max={96}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('button.borderWidth')}
            error={errors.advanced?.button?.border_width_px?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.button.border_width_px"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={WIDGET_BORDER_WIDTH_MAX}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('button.borderColor')}
            error={errors.advanced?.button?.border_color?.message}
          >
            <Controller
              control={control}
              name="advanced.button.border_color"
              render={({ field }) => (
                <ColorField value={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
        </div>
      </div>

      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('preview.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          <PresetFormItem
            label={t('preview.messageRadius')}
            error={errors.advanced?.preview?.message_border_radius?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.preview.message_border_radius"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={40}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem
            label={t('preview.hintRadius')}
            error={errors.advanced?.preview?.hint_border_radius?.message}
            fullWidth
          >
            <Controller
              control={control}
              name="advanced.preview.hint_border_radius"
              render={({ field }) => (
                <SliderNumberField
                  value={field.value}
                  min={0}
                  max={40}
                  disabled={disabled}
                  addonAfter="px"
                  onChange={field.onChange}
                />
              )}
            />
          </PresetFormItem>
          <PresetFormItem label={t('preview.agentHints')} fullWidth>
            <Controller
              control={control}
              name="advanced.preview.show_agent_answer_hints"
              render={({ field }) => (
                <Switch checked={field.value} disabled={disabled} onChange={field.onChange} />
              )}
            />
          </PresetFormItem>
        </div>
      </div>

      <div className={styles.subsection}>
        <Text variant="sm" fontWeight={600} className={styles.subsectionTitle}>
          {t('position.title')}
        </Text>
        <div className={styles.fieldsGrid}>
          {(['desktop', 'mobile'] as const).map((device) => (
            <div key={device} className={styles.deviceCard}>
              <Text variant="sm" fontWeight={500}>
                {t(`position.${device}`)}
              </Text>
              <PresetFormItem label={t('position.placement')} required>
                <Controller
                  control={control}
                  name={`advanced.position.${device}.placement`}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={disabled}
                      placeholder={t('position.placementPlaceholder')}
                      options={PLACEMENTS.map((placement) => ({
                        label: t(`position.placements.${placement}`),
                        value: placement,
                      }))}
                      onChange={field.onChange}
                    />
                  )}
                />
              </PresetFormItem>
              <Flex gap={8}>
                <PresetFormItem
                  label={t('position.sideOffset')}
                  error={errors.advanced?.position?.[device]?.side_offset?.message}
                >
                  <Controller
                    control={control}
                    name={`advanced.position.${device}.side_offset`}
                    render={({ field }) => (
                      <InputNumber
                        {...field}
                        min={0}
                        max={500}
                        addonAfter="px"
                        disabled={disabled}
                        placeholder={t('position.offsetPlaceholder')}
                        className={styles.numberInput}
                      />
                    )}
                  />
                </PresetFormItem>
                <PresetFormItem
                  label={t('position.bottomOffset')}
                  error={errors.advanced?.position?.[device]?.bottom_offset?.message}
                >
                  <Controller
                    control={control}
                    name={`advanced.position.${device}.bottom_offset`}
                    render={({ field }) => (
                      <InputNumber
                        {...field}
                        min={0}
                        max={500}
                        addonAfter="px"
                        disabled={disabled}
                        placeholder={t('position.offsetPlaceholder')}
                        className={styles.numberInput}
                      />
                    )}
                  />
                </PresetFormItem>
              </Flex>
            </div>
          ))}
        </div>
      </div>
    </Flex>
  );
};
